<template>
  <div class="incident-form-root">
    <div
      v-if="localError"
      class="card-panel red lighten-5 red-text text-darken-3"
      style="margin-top: 10px"
    >
      {{ localError }}
    </div>

    <div
      v-if="successNo"
      class="card-panel green lighten-5 green-text text-darken-3"
      style="margin-top: 10px"
    >
      Created incident <strong>#{{ formatIncidentNo(successNo) }}</strong
      >.
    </div>

    <div class="row" style="margin-top: 8px; margin-bottom: 0">
      <!-- Venue -->
      <div class="input-field col s12 m6">
        <input id="venueNameDisplay" :value="venueName" type="text" disabled />
        <label class="active" for="venueNameDisplay">Venue</label>
      </div>

      <!-- Report Started -->
      <div class="input-field col s12 m6">
        <input
          id="reportStartedAtDisplay"
          :value="reportStartedAtDisplay"
          type="text"
          disabled
        />
        <label class="active" for="reportStartedAtDisplay">Report started</label>
      </div>

      <!-- Reporter Name -->
      <div class="input-field col s12 m6">
        <input
          id="reporterName"
          v-model="reporterName"
          type="text"
          :disabled="busy"
          maxlength="120"
        />
        <label class="active" for="reporterName">Completed By</label>
      </div>

      <!-- Reporter Role -->
      <div class="input-field col s12 m6">
        <input
          id="reporterRole"
          v-model="reporterRole"
          type="text"
          :disabled="busy"
          maxlength="80"
        />
        <label class="active" for="reporterRole">Job Role</label>
      </div>

      <!-- Category -->
      <div class="input-field col s12 m6">
        <select ref="categorySelectEl" v-model="category" :disabled="busy">
          <option value="" disabled>Select category</option>
          <option v-for="opt in categoryOptions" :key="opt" :value="opt">
            {{ opt }}
          </option>
        </select>
        <label class="active">Category</label>
      </div>

      <!-- Incident At -->
      <div class="input-field col s12 m6">
        <input
          id="incidentAt"
          v-model="incidentAt"
          type="datetime-local"
          :disabled="busy"
        />
        <label class="active" for="incidentAt">Incident time</label>
      </div>

      <!-- Details -->
      <div class="input-field col s12">
        <textarea
          id="details"
          v-model="details"
          class="materialize-textarea"
          :disabled="busy"
          rows="8"
        ></textarea>
        <label class="active" for="details">Incident details</label>
      </div>

      <!-- CCTV -->
      <div class="col s12 m6" style="padding-top: 18px">
        <label style="display: flex; align-items: center; gap: 10px">
          <input
            type="checkbox"
            class="filled-in"
            v-model="capturedOnCctv"
            :disabled="busy"
          />
          <span>Captured on CCTV</span>
        </label>
      </div>

      <!-- Media upload -->
      <div class="col s12" style="margin-top: 10px">
        <div style="font-weight: 600; margin-bottom: 8px">Images / Video</div>

        <div class="file-field input-field">
          <div class="btn blue-grey darken-2" :class="{ disabled: busy }">
            <span>Choose files</span>
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              :disabled="busy"
              @change="onFilesSelected"
            />
          </div>
          <div class="file-path-wrapper">
            <input
              class="file-path validate"
              type="text"
              placeholder="Upload incident images or video"
              :disabled="busy"
            />
          </div>
        </div>

        <div v-if="mediaFiles.length" class="chip-row">
          <div
            v-for="(item, idx) in mediaFiles"
            :key="item.name + idx"
            class="chip media-chip"
          >
            {{ shorten(item.name) }}
            <i class="close material-icons" @click="removeMedia(idx)">close</i>
          </div>
        </div>

        <div v-if="mediaPreview.length" class="media-preview-grid">
          <div
            v-for="(item, idx) in mediaPreview"
            :key="item.name + idx"
            class="media-preview-card"
          >
            <img
              v-if="item.kind === 'image'"
              :src="item.url"
              class="preview-image materialboxed"
              :alt="item.name"
            />
            <video
              v-else
              :src="item.url"
              class="preview-video"
              controls
              preload="metadata"
            ></video>

            <div class="preview-name">{{ item.name }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="right-align" style="margin-top: 18px">
      <button
        class="btn blue-grey darken-2"
        type="button"
        :disabled="busy || !canSubmit"
        @click="submit"
      >
        {{ busy ? "Submitting…" : "Submit incident" }}
      </button>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { auth, db, storage } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";

export default {
  name: "IncidentReportForm",
  props: {
    venueId: { type: String, required: true },
  },
  emits: ["created", "error", "busy"],
  setup(props, { emit, expose }) {
    const categoryOptions = [
      "Crime / Violence",
      "Theft",
      "Drugs",
      "Medical",
      "Ejection / Refusal",
      "Accident",
      "Licensing",
      "Welfare",
      "Damage",
      "Other",
    ];

    const category = ref("");
    const capturedOnCctv = ref(false);
    const incidentAt = ref("");
    const reportStartedAt = ref(null);
    const reportStartedAtDisplay = ref("");
    const reporterName = ref("");
    const reporterRole = ref("");
    const venueName = ref("");
    const details = ref("");

    const mediaFiles = ref([]);
    const mediaPreview = ref([]);

    const busy = ref(false);
    const localError = ref("");
    const successNo = ref(null);
    const categorySelectEl = ref(null);

    const canSubmit = computed(() => {
      return category.value.trim().length > 0 && details.value.trim().length >= 10;
    });

    function formatIncidentNo(n) {
      if (n === undefined || n === null) return "—";
      const asInt = Number(n);
      if (Number.isNaN(asInt)) return String(n);
      return String(asInt).padStart(6, "0");
    }

    function shorten(s) {
      if (!s) return "";
      return s.length > 34 ? `${s.slice(0, 34)}…` : s;
    }

    function toDateOrNull(v) {
      if (!v) return null;
      const dt = v instanceof Date ? v : new Date(v);
      return Number.isNaN(dt.getTime()) ? null : dt;
    }

    function pad2(n) {
      return String(n).padStart(2, "0");
    }

    function formatDateTimeDisplay(v) {
      const dt = v instanceof Date ? v : new Date(v);
      if (Number.isNaN(dt.getTime())) return "";

      return `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}/${dt.getFullYear()} ${pad2(
        dt.getHours()
      )}:${pad2(dt.getMinutes())}`;
    }

    function stampReportStartedNow() {
      const now = new Date();
      reportStartedAt.value = now;
      reportStartedAtDisplay.value = formatDateTimeDisplay(now);

      nextTick(() => {
        if (window.M) {
          window.M.updateTextFields();
        }
      });
    }

    expose({
      stampReportStartedNow,
    });

    async function loadVenueName() {
      try {
        const venueRef = doc(db, "venues", props.venueId);
        const snap = await getDoc(venueRef);
        const data = snap.exists() ? snap.data() || {} : {};
        venueName.value =
          data.name || data.venue_name || data.displayName || props.venueId;
      } catch {
        venueName.value = props.venueId;
      }
    }

    function initMaterializeBits() {
      if (!window.M) return;

      window.M.updateTextFields();

      if (categorySelectEl.value) {
        const existing = window.M.FormSelect.getInstance(categorySelectEl.value);
        if (existing) existing.destroy();
        window.M.FormSelect.init(categorySelectEl.value);
      }

      nextTick(() => {
        const boxed = document.querySelectorAll(".materialboxed");
        if (boxed.length) {
          window.M.Materialbox.init(boxed);
        }
      });
    }

    function revokePreviewUrls() {
      mediaPreview.value.forEach((item) => {
        if (item?.url) URL.revokeObjectURL(item.url);
      });
    }

    function onFilesSelected(event) {
      const files = Array.from(event?.target?.files || []);
      if (!files.length) return;

      const valid = files.filter((f) => {
        const t = String(f.type || "");
        return t.startsWith("image/") || t.startsWith("video/");
      });

      mediaFiles.value = [...mediaFiles.value, ...valid];

      const newPreview = valid.map((file) => ({
        name: file.name,
        kind: file.type.startsWith("video/") ? "video" : "image",
        url: URL.createObjectURL(file),
      }));

      mediaPreview.value = [...mediaPreview.value, ...newPreview];

      nextTick(() => initMaterializeBits());

      event.target.value = "";
    }

    function removeMedia(idx) {
      const preview = mediaPreview.value[idx];
      if (preview?.url) {
        URL.revokeObjectURL(preview.url);
      }

      mediaFiles.value.splice(idx, 1);
      mediaPreview.value.splice(idx, 1);
    }

    function reset() {
      category.value = "";
      capturedOnCctv.value = false;
      incidentAt.value = "";
      reportStartedAt.value = null;
      reportStartedAtDisplay.value = "";
      reporterName.value = "";
      reporterRole.value = "";
      details.value = "";
      localError.value = "";

      revokePreviewUrls();
      mediaFiles.value = [];
      mediaPreview.value = [];

      nextTick(() => initMaterializeBits());
    }

    async function uploadMediaFiles(incidentNumber) {
      const uploads = [];

      for (const file of mediaFiles.value) {
        const safeName = file.name.replace(/[^\w.\-]+/g, "_");
        const path = [
          "incident_reports",
          props.venueId,
          String(incidentNumber).padStart(6, "0"),
          `${Date.now()}_${safeName}`,
        ].join("/");

        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, file);
        const url = await getDownloadURL(fileRef);

        uploads.push({
          name: file.name,
          type: file.type || "",
          size: Number(file.size || 0),
          url,
          storagePath: path,
          kind: String(file.type || "").startsWith("video/") ? "video" : "image",
        });
      }

      return uploads;
    }

    async function submit() {
      localError.value = "";
      successNo.value = null;

      const user = auth.currentUser;
      if (!user) {
        localError.value = "You must be signed in to submit an incident report.";
        emit("error", localError.value);
        return;
      }

      busy.value = true;
      emit("busy", true);

      try {
        const counterRef = doc(db, "counters", "incidents");
        const venueRef = doc(db, "venues", props.venueId);
        const incidentsCol = collection(venueRef, "incident_reports");
        const newIncidentRef = doc(incidentsCol);

        const nextIncidentNumber = await runTransaction(db, async (tx) => {
          const counterSnap = await tx.get(counterRef);

          let next = 1;
          if (counterSnap.exists()) {
            next = Number(counterSnap.data()?.next || 1);
          }

          tx.set(counterRef, { next: next + 1 }, { merge: true });
          return next;
        });

        const uploadedMedia = await uploadMediaFiles(nextIncidentNumber);

        await runTransaction(db, async (tx) => {
          tx.set(newIncidentRef, {
            incidentNumber: nextIncidentNumber,
            venueId: props.venueId,
            venueName: venueName.value.trim() || props.venueId,
            category: category.value.trim(),
            capturedOnCctv: !!capturedOnCctv.value,
            details: details.value.trim(),
            incidentAt: toDateOrNull(incidentAt.value),
            reportStartedAt: toDateOrNull(reportStartedAt.value),
            reporterName: reporterName.value.trim(),
            reporterRole: reporterRole.value.trim(),
            createdAt: serverTimestamp(),
            createdByUid: user.uid,
            readBy: [user.uid],
            photos: uploadedMedia.filter((m) => m.kind === "image").map((m) => m.url),
            media: uploadedMedia,
          });
        });

        successNo.value = nextIncidentNumber;
        emit("created", {
          incidentNumber: nextIncidentNumber,
          id: newIncidentRef.id,
        });

        const createdNo = nextIncidentNumber;
        reset();
        successNo.value = createdNo;

        if (window.M?.toast) {
          window.M.toast({
            html: `Created incident #${formatIncidentNo(createdNo)}`,
          });
        }
      } catch (e) {
        localError.value = e?.message || "Failed to submit incident report.";
        emit("error", localError.value);
      } finally {
        busy.value = false;
        emit("busy", false);
      }
    }

    onMounted(async () => {
      await loadVenueName();
      initMaterializeBits();
    });

    watch(() => props.venueId, loadVenueName);

    onBeforeUnmount(() => {
      revokePreviewUrls();
    });

    return {
      category,
      categoryOptions,
      capturedOnCctv,
      incidentAt,
      reportStartedAt,
      reportStartedAtDisplay,
      reporterName,
      reporterRole,
      venueName,
      details,
      mediaFiles,
      mediaPreview,
      busy,
      localError,
      successNo,
      canSubmit,
      categorySelectEl,
      formatIncidentNo,
      shorten,
      onFilesSelected,
      removeMedia,
      submit,
      stampReportStartedNow,
    };
  },
};
</script>

<style scoped>
.header-action-col {
  margin-top: 8px;
}

.incident-modal {
  width: 96%;
  max-width: 1200px;
  max-height: 1500px;
  height: 1500px;
  overflow-y: auto;
}

.photo-preview-grid,
.video-preview-grid {
  margin-top: 10px;
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (min-width: 993px) {
  .photo-preview-grid,
  .video-preview-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.photo-preview-grid img,
.video-preview-grid video {
  width: 100%;
  height: 160px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

/* Mobile optimisation only */
.incident-form-root {
  width: 100%;
}

@media (max-width: 600px) {
  .incident-form-root .row {
    margin-bottom: 0;
  }

  .incident-form-root .input-field {
    margin-top: 10px;
    margin-bottom: 6px;
  }

  .incident-form-root .file-field .btn {
    width: 100%;
    margin-bottom: 8px;
  }

  .incident-form-root .file-path-wrapper {
    padding-left: 0;
  }

  .incident-form-root .chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .incident-form-root .media-chip {
    max-width: 100%;
    overflow-wrap: anywhere;
    height: auto;
    min-height: 32px;
    line-height: 1.3;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .incident-form-root .media-preview-grid {
    grid-template-columns: 1fr;
  }

  .incident-form-root .preview-image,
  .incident-form-root .preview-video {
    height: 190px;
  }

  .incident-form-root .right-align {
    text-align: left !important;
  }

  .incident-form-root .right-align .btn {
    width: 100%;
  }
}
</style>
